import { useState } from "react";
import type { VitalKind, VitalSignPayload } from "@medassist/shared";
import { VITAL_DEFAULT_UNIT } from "@medassist/shared";
import { saveBatchEvents, ApiError } from "../../../lib/api.js";
import { relativeTime } from "../../../lib/time.js";
import { Select } from "../../Select.js";
import { HeartPulseIcon } from "../../icons.js";
import type { WidgetProps } from "../types.js";

const SNAPSHOT_KINDS: { kind: VitalKind; label: string }[] = [
  { kind: "bp",      label: "Blood pressure" },
  { kind: "hr",      label: "Heart rate" },
  { kind: "temp",    label: "Temperature" },
  { kind: "spo2",    label: "Blood oxygen" },
  { kind: "glucose", label: "Glucose" },
];

const LOGGABLE_KINDS: VitalKind[] = ["bp", "hr", "temp", "spo2", "glucose"];

function vitalNumber(p: VitalSignPayload): number | undefined {
  return p.kind === "bp" ? p.systolic : p.value;
}

function vitalDisplay(p: VitalSignPayload): string {
  if (p.kind === "bp") return `${p.systolic ?? "?"}/${p.diastolic ?? "?"} ${p.unit || "mmHg"}`;
  return `${p.value ?? "?"} ${p.unit || VITAL_DEFAULT_UNIT[p.kind]}`;
}

export function VitalsWidget({ patient, events, onNavigate, onError, onPrependEvent, onShowToast }: WidgetProps) {
  const [vKind, setVKind] = useState<VitalKind>("bp");
  const [vSys,  setVSys]  = useState("");
  const [vDia,  setVDia]  = useState("");
  const [vVal,  setVVal]  = useState("");
  const [vBusy, setVBusy] = useState(false);

  function latestVital(kind: VitalKind) {
    const matches = events.filter(
      (e) => e.eventType === "vital_sign" && (e.payload as unknown as VitalSignPayload).kind === kind
    );
    return {
      latest: matches[0]?.payload as unknown as VitalSignPayload | undefined,
      prev:   matches[1]?.payload as unknown as VitalSignPayload | undefined,
      at:     matches[0]?.occurredAt,
    };
  }

  async function logVital() {
    const measuredAt = new Date().toISOString();
    let payload: Record<string, unknown>;
    if (vKind === "bp") {
      const s = parseFloat(vSys);
      const d = parseFloat(vDia);
      if (!isFinite(s) || !isFinite(d)) return;
      payload = { kind: "bp", systolic: s, diastolic: d, unit: "mmHg", measuredAt };
    } else {
      const v = parseFloat(vVal);
      if (!isFinite(v)) return;
      payload = { kind: vKind, value: v, unit: VITAL_DEFAULT_UNIT[vKind], measuredAt };
    }
    setVBusy(true);
    onError(null);
    try {
      const [ref] = await saveBatchEvents(patient.patientId, [{ eventType: "vital_sign", payload }]);
      onPrependEvent(ref, payload);
      setVSys(""); setVDia(""); setVVal("");
      onShowToast(ref, `Logged ${vitalDisplay(payload as unknown as VitalSignPayload)}`);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Couldn't log reading");
    } finally {
      setVBusy(false);
    }
  }

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 sm:col-span-2">
      <div className="mb-2 flex items-center gap-2">
        <HeartPulseIcon size={16} className="text-(--accent)" />
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Vitals</h2>
        <button
          onClick={() => onNavigate("charts")}
          className="ml-auto text-xs text-(--accent) hover:underline"
        >
          View charts
        </button>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
        {SNAPSHOT_KINDS.map(({ kind, label }) => {
          const { latest, prev, at } = latestVital(kind);
          const cur = latest ? vitalNumber(latest) : undefined;
          const old = prev ? vitalNumber(prev) : undefined;
          const arrow = cur != null && old != null ? (cur > old ? "↑" : cur < old ? "↓" : "→") : "";
          return (
            <div key={kind} className="rounded-lg bg-gray-50 dark:bg-gray-800/60 px-3 py-2">
              <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
              {latest ? (
                <>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">
                    {vitalDisplay(latest)}{" "}
                    {arrow && <span className="text-xs text-gray-400 dark:text-gray-500">{arrow}</span>}
                  </p>
                  {at && <p className="text-[11px] text-gray-400 dark:text-gray-500">{relativeTime(at)}</p>}
                </>
              ) : (
                <p className="text-sm text-gray-400 dark:text-gray-500">No readings</p>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-2 border-t border-gray-100 dark:border-gray-800 pt-3">
        <label className="flex flex-col text-xs text-gray-500 dark:text-gray-400">
          Log
          <Select
            value={vKind}
            onChange={(e) => setVKind(e.target.value as VitalKind)}
            className="mt-0.5"
          >
            {LOGGABLE_KINDS.map((k) => (
              <option key={k} value={k}>{SNAPSHOT_KINDS.find((s) => s.kind === k)?.label ?? k}</option>
            ))}
          </Select>
        </label>
        {vKind === "bp" ? (
          <>
            <input
              type="number" inputMode="numeric" placeholder="Sys" value={vSys}
              onChange={(e) => setVSys(e.target.value)}
              className="w-20 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
            />
            <span className="pb-1 text-gray-400">/</span>
            <input
              type="number" inputMode="numeric" placeholder="Dia" value={vDia}
              onChange={(e) => setVDia(e.target.value)}
              className="w-20 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
            />
          </>
        ) : (
          <input
            type="number" inputMode="decimal" step="any"
            placeholder={VITAL_DEFAULT_UNIT[vKind]} value={vVal}
            onChange={(e) => setVVal(e.target.value)}
            className="w-28 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
          />
        )}
        <button
          onClick={() => void logVital()}
          disabled={vBusy}
          className="rounded-md bg-(--accent) px-3 py-1.5 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-50"
        >
          {vBusy ? "Saving…" : "Log"}
        </button>
      </div>
    </section>
  );
}
